import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, type DrawerProps, Grid, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type DrawerIncomeFormType, getDrawerIncomeFormSchema } from "Forms/Schemas/Budget/Incomes/drawerIncomeFormSchema";
import { type AnalysisClientItem, type Income, IncomeIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { type FC, useContext, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { DisabilityIncomeForm } from "../Forms/DisabilityIncomeForm";
import { EmploymentIncomeForm } from "../Forms/EmploymentIncomeForm";
import { OrphanOrWidowIncomeForm } from "../Forms/OrphanOrWidowIncomeForm";
import { OtherIncomeForm } from "../Forms/OtherIncomeForm";
import { ParentalIncomeForm } from "../Forms/ParentalIncomeForm";
import { PensionIncomeForm } from "../Forms/PensionIncomeForm";
import { RentalIncomeForm } from "../Forms/RentalIncomeForm";
import { SelfEmploymentIncomeForm } from "../Forms/SelfEmploymentIncomeForm";
import { IncomeTypeTile } from "./IncomeTypeTile";

type Props = {
  income: Income;
  onClose: () => void;
  clients?: AnalysisClientItem[];
} & DrawerProps;

export type SubmitIncomeFormHandle = {
  readonly submitForm: () => void;
  readonly deleteIncome: () => void;
};

export const EditIncomeDrawer: FC<Props> = ({ income, onClose, clients, ...rest }) => {
  const { readOnly } = useContext(AnalysisDetailContext);

  const theme = useTheme();
  const { t } = useResource();
  const [incomeType, setIncomeType] = useState<IncomeIncomeType>(income.incomeType);

  const employmentIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const selfEmploymentIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const rentalIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const parentalIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const pensionIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const disabilityIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const orphanOrWidowIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const otherIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);

  const clientOptions: Option[] = (clients ?? []).map((client) => ({
    value: client.guid,
    name: formatClientName(client.firstName, client.surname, client.birthDate ?? null),
  }));

  const {
    control,
    watch,
    formState: { errors },
  } = useForm<DrawerIncomeFormType>({
    resolver: yupResolver(getDrawerIncomeFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      clientId: income.clientGuid,
    },
  });

  const selectedClientId = watch("clientId");

  return (
    <Drawer {...rest} onClose={onClose}>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
        <Typography variant="h2">{t(Resources.AnalysisDetail.Budget.Incomes.Edit)}</Typography>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DrawerContent>
        <Box px={Spacing.XXL} mb={Spacing.XXL}>
          <FormDropdown
            control={control}
            disabled={readOnly}
            name="clientId"
            labelEndAdornment={<StyledLabelAdornment />}
            label={t(Resources.AnalysisDetail.Budget.Incomes.IncomeBelongsTo)}
            errors={errors}
            options={clientOptions}
          />

          <Divider />
        </Box>

        <Box px={Spacing.XXL} mb={Spacing.XXL}>
          <Stack spacing={Spacing.XL}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t(Resources.AnalysisDetail.Budget.Incomes.IncomeType)}
            </Typography>

            <Grid container spacing={Spacing.M}>
              {(Object.values(IncomeIncomeType) as Array<IncomeIncomeType>).map((value) => {
                return (
                  <Grid key={value} size={3}>
                    <IncomeTypeTile
                      active={value === incomeType}
                      disabled={value !== incomeType}
                      incomeType={value}
                      onIncomeSelect={(type) => {
                        setIncomeType(type);
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Box>

        <Box px={Spacing.XXL} mb={Spacing.XXL}>
          {incomeType === IncomeIncomeType.employment && (
            <EmploymentIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={employmentIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.self_employment && (
            <SelfEmploymentIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={selfEmploymentIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.rental && (
            <RentalIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={rentalIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.parental && (
            <ParentalIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={parentalIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.pension && (
            <PensionIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={pensionIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.disability && (
            <DisabilityIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={disabilityIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.orphan_or_widow_pension && (
            <OrphanOrWidowIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={orphanOrWidowIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.other && (
            <OtherIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={otherIncomeFormRef} />
          )}
        </Box>
      </DrawerContent>

      <Box display="flex" flexDirection="row" justifyContent="space-between" py={Spacing.XXL} px={Spacing.XXL}>
        <Button
          disabled={readOnly}
          variant="text"
          colorCustom={theme.colors.brandPrimary}
          onClick={() => {
            if (incomeType === IncomeIncomeType.employment) {
              employmentIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.self_employment) {
              selfEmploymentIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.rental) {
              rentalIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.parental) {
              parentalIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.pension) {
              pensionIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.disability) {
              disabilityIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.orphan_or_widow_pension) {
              orphanOrWidowIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.other) {
              otherIncomeFormRef.current?.deleteIncome();
            }
          }}
        >
          {t(Resources.Common.Delete)}
        </Button>

        <Stack direction="row" spacing={Spacing.XL}>
          <Button
            onClick={onClose}
            color="secondary"
            sx={{
              marginRight: Spacing.XL,
            }}
          >
            {t(Resources.Common.Close)}
          </Button>

          <Button
            disabled={!selectedClientId || readOnly}
            type="submit"
            onClick={() => {
              if (incomeType === IncomeIncomeType.employment) {
                employmentIncomeFormRef.current?.submitForm();
              }
              if (incomeType === IncomeIncomeType.self_employment) {
                selfEmploymentIncomeFormRef.current?.submitForm();
              }
              if (incomeType === IncomeIncomeType.rental) {
                rentalIncomeFormRef.current?.submitForm();
              }
              if (incomeType === IncomeIncomeType.parental) {
                parentalIncomeFormRef.current?.submitForm();
              }
              if (incomeType === IncomeIncomeType.pension) {
                pensionIncomeFormRef.current?.submitForm();
              }
              if (incomeType === IncomeIncomeType.disability) {
                disabilityIncomeFormRef.current?.submitForm();
              }
              if (incomeType === IncomeIncomeType.orphan_or_widow_pension) {
                orphanOrWidowIncomeFormRef.current?.submitForm();
              }
              if (incomeType === IncomeIncomeType.other) {
                otherIncomeFormRef.current?.submitForm();
              }
            }}
          >
            {t(Resources.Common.Save)}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};
